import { useEffect, useState } from 'react';
import { assetsApi, type Asset } from '../lib/api/assets';
import { attachmentsApi } from '../lib/api/attachments';
import { AttachmentImagePreview } from './AttachmentImagePreview';
import { formatVND } from '../lib/format';

interface Props {
  open: boolean;
  assetId: string | null;
  onClose: () => void;
  onEdit?: (id: string) => void;
  onDeleted?: () => void;
  canEdit: boolean;
  canDelete: boolean;
}

const METHOD_LABEL: Record<string, string> = {
  straight: 'Đường thẳng (SLN)',
  declining: 'Số dư giảm dần',
  units: 'Theo sản lượng',
};

const STATUS_LABEL: Record<Asset['status'], { label: string; bg: string; fg: string }> = {
  in_use: { label: 'Đang dùng', bg: '#dcfce7', fg: '#15803d' },
  depleted: { label: 'Hết khấu hao', bg: '#f1f5f9', fg: '#475569' },
  maintenance: { label: 'Bảo trì', bg: '#fef3c7', fg: '#a16207' },
  disposed: { label: 'Đã thanh lý', bg: '#fee2e2', fg: '#b91c1c' },
};

export function AssetDetailModal({ open, assetId, onClose, onEdit, onDeleted, canEdit, canDelete }: Props) {
  const [asset, setAsset] = useState<Asset | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !assetId) return;
    setLoading(true);
    setErr(null);
    setAsset(null);
    assetsApi.findOne(assetId)
      .then(setAsset)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [open, assetId]);

  if (!open) return null;

  const handleDelete = async () => {
    if (!asset) return;
    if (!window.confirm(`Xoá TS ${asset.code} - ${asset.name}?`)) return;
    setDeleting(true);
    try {
      await assetsApi.remove(asset.id);
      onDeleted?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setDeleting(false); }
  };

  const status = asset ? STATUS_LABEL[asset.status] : null;

  return (
    <div onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={headerIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round"><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M8 12h8M12 8v8" /></svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#172033' }}>
                  {asset ? asset.code : 'Chi tiết tài sản'}
                </h3>
                {status && (
                  <span style={{ padding: '2px 8px', background: status.bg, color: status.fg, fontSize: 11, fontWeight: 600, borderRadius: 9999 }}>{status.label}</span>
                )}
              </div>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#667085', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {asset?.name || 'Đang tải…'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeBtn} aria-label="Đóng">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div style={body}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {loading && <div style={{ padding: 20, textAlign: 'center', color: '#64748b' }}>Đang tải…</div>}
            {err && <div style={errBox}>{err}</div>}

            {asset && (
              <>
                {/* KPI: Nguyên giá + Đã KH + GTCL */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  <MiniKpi label="Nguyên giá" value={formatVND(asset.cost)} tone="gray" />
                  <MiniKpi label="Đã khấu hao" value={`${asset.depreciatedPct}%`} tone="amber" />
                  <MiniKpi label="Giá trị còn lại" value={formatVND(asset.remaining)} tone="green" />
                </div>

                {/* Depreciation progress bar */}
                <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                    <span>Tiến độ khấu hao</span>
                    <span><b style={{ color: '#172033' }}>{asset.depreciatedPct}%</b> · còn <b style={{ color: '#15803d' }}>{100 - asset.depreciatedPct}%</b></span>
                  </div>
                  <div style={{ height: 8, background: '#f1f5f9', borderRadius: 9999, overflow: 'hidden' }}>
                    <div style={{ width: `${asset.depreciatedPct}%`, height: '100%', background: asset.depreciatedPct >= 70 ? '#f59e0b' : '#22c55e' }} />
                  </div>
                </div>

                {/* Section: Thông tin cơ bản */}
                <DetailSection title="Thông tin cơ bản">
                  <DetailRow label="Loại" value={asset.category ? `${asset.category.code} · ${asset.category.name}` : asset.categoryCode} />
                  <DetailRow label="Serial number" value={asset.serialNumber} mono />
                </DetailSection>

                {/* Section: Mua sắm */}
                <DetailSection title="Mua sắm">
                  <DetailRow label="Nhà cung cấp" value={asset.supplier} />
                  <DetailRow label="Ngày mua" value={formatDate(asset.purchasedAt)} mono />
                  <DetailRow label="Số hoá đơn" value={asset.invoiceNumber} mono />
                  <DetailRow label="Bảo hành đến" value={formatDate(asset.warrantyUntil)} mono />
                </DetailSection>

                {/* Section: Khấu hao */}
                <DetailSection title="Cấu hình khấu hao">
                  <DetailRow label="Phương pháp" value={asset.depreciationMethod ? METHOD_LABEL[asset.depreciationMethod] : '—'} />
                  <DetailRow
                    label="Thời gian KH"
                    value={
                      asset.depreciationYears != null
                        ? `${asset.depreciationYears} năm`
                        : asset.category
                          ? `${asset.category.years} năm (mặc định)`
                          : '—'
                    }
                  />
                  <DetailRow label="Ngày bắt đầu KH" value={formatDate(asset.depreciationStartDate)} mono />
                  <DetailRow label="TK nguyên giá" value={asset.costAccount ?? (asset.category?.account ?? '—')} mono />
                  <DetailRow label="TK hao mòn" value={asset.depreciationAccount} mono />
                </DetailSection>

                {/* Section: Gán cho */}
                <DetailSection title="Gán cho">
                  <DetailRow label="Bộ phận" value={asset.department} />
                  <DetailRow label="Nhân viên sử dụng" value={asset.userName} />
                  <DetailRow label="Vị trí" value={asset.location} />
                </DetailSection>

                {asset.images && asset.images.length > 0 && (
                  <DetailSection title={`Hình ảnh tài sản (${asset.images.length})`}>
                    <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(135px, 1fr))', gap: 10 }}>
                      {asset.images.map((image) => (
                        <button key={image.id} type="button" onClick={() => attachmentsApi.openInNewTab(image.id, image.fileName).catch((error) => setErr(error instanceof Error ? error.message : 'Không thể mở ảnh.'))} style={{ minHeight: 94, padding: 10, border: '1px solid #dbe3ee', borderRadius: 8, background: '#f8fafc', color: '#1d4ed8', cursor: 'pointer', textAlign: 'left' }}>
                          <AttachmentImagePreview attachment={image} alt={image.fileName} style={{ height: 44, borderRadius: 6 }} />
                          <div title={image.fileName} style={{ marginTop: 7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, fontWeight: 600 }}>{image.fileName}</div>
                        </button>
                      ))}
                    </div>
                  </DetailSection>
                )}

                <DetailSection title={`Lịch sử bàn giao (${asset.assignmentHistory?.length ?? 0})`}>
                  {asset.assignmentHistory?.length ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {asset.assignmentHistory.map((item) => (
                        <div key={item.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 10, alignItems: 'start', paddingBottom: 10, borderBottom: '1px solid #eef2f7' }}>
                          <div style={{ width: 24, height: 24, borderRadius: 999, display: 'grid', placeItems: 'center', background: item.unassignedAt ? '#f1f5f9' : '#dcfce7', color: item.unassignedAt ? '#64748b' : '#15803d', fontSize: 12 }}>{item.unassignedAt ? '↔' : '✓'}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#172033' }}>{item.employeeName ?? 'Dùng chung / chưa gán'}</div>
                            <div style={{ marginTop: 2, fontSize: 12, color: '#64748b' }}>{[item.department, item.location].filter(Boolean).join(' · ') || 'Chưa ghi nhận bộ phận/vị trí'}</div>
                            <div style={{ marginTop: 3, fontSize: 11, color: '#94a3b8' }}>Bàn giao {formatDateTime(item.assignedAt)}{item.assignedBy ? ` · ${item.assignedBy}` : ''}</div>
                          </div>
                          <span style={{ padding: '3px 7px', borderRadius: 999, background: item.unassignedAt ? '#f1f5f9' : '#ecfdf3', color: item.unassignedAt ? '#64748b' : '#15803d', fontSize: 10, fontWeight: 700 }}>{item.unassignedAt ? `Đã thu hồi ${formatDate(item.unassignedAt)}` : 'Đang sử dụng'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>Chưa có lịch sử bàn giao. Lần chọn nhân viên từ HR tiếp theo sẽ được ghi nhận tại đây.</div>
                  )}
                </DetailSection>

                {/* Section: Ghi chú */}
                {asset.notes && (
                  <DetailSection title="Ghi chú">
                    <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{asset.notes}</div>
                  </DetailSection>
                )}
              </>
            )}
          </div>
        </div>

        <div style={footer}>
          {canDelete && asset ? (
            <button type="button" onClick={handleDelete} disabled={deleting} style={deleteBtn}>
              {deleting ? 'Đang xoá…' : 'Xoá tài sản'}
            </button>
          ) : <div />}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Đóng</button>
            {canEdit && asset && (
              <button type="button" onClick={() => onEdit?.(asset.id)} style={editBtn}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                Sửa tài sản
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#667085', fontWeight: 700, marginBottom: 10 }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 8, columnGap: 16 }}>{children}</div>
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  const display = value == null || value === '' ? '—' : String(value);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', padding: '4px 0' }}>
      <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: 13, color: display === '—' ? '#94a3b8' : '#172033', fontWeight: display === '—' ? 400 : 500, fontFamily: mono ? 'inherit' : undefined, textAlign: 'right' }}>{display}</span>
    </div>
  );
}

function MiniKpi({ label, value, tone }: { label: string; value: string; tone: 'gray' | 'amber' | 'green' }) {
  const colors: Record<string, { bg: string; fg: string; border: string }> = {
    gray: { bg: '#fff', fg: '#172033', border: '#e4e7ec' },
    amber: { bg: '#fef3c7', fg: '#a16207', border: '#fde68a' },
    green: { bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0' },
  };
  const c = colors[tone];
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: '#667085', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: c.fg }}>{value}</div>
    </div>
  );
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('vi-VN');
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' });
}

// ==== Styles ====

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
  zIndex: 60, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalBox: React.CSSProperties = {
  width: 760, maxWidth: '100%', height: 'calc(100vh - 48px)', maxHeight: 820,
  display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12,
  boxShadow: '0 25px 60px -12px rgba(15,23,42,.5)', overflow: 'hidden',
};
const header: React.CSSProperties = {
  flex: '0 0 auto', padding: '18px 24px', borderBottom: '1px solid #e4e7ec',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const headerIcon: React.CSSProperties = {
  width: 38, height: 38, borderRadius: 9, background: 'linear-gradient(135deg,#f5f3ff,#ede9fe)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
};
const closeBtn: React.CSSProperties = {
  width: 32, height: 32, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8,
  cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', overflowX: 'hidden', background: '#f8fafc', minHeight: 0 };
const footer: React.CSSProperties = {
  flex: '0 0 auto', padding: '14px 24px', borderTop: '1px solid #e4e7ec', background: '#fff',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const errBox: React.CSSProperties = {
  padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
  color: '#b91c1c', fontSize: 13,
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 16px', background: '#fff', border: '1px solid #e4e7ec', color: '#334155',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const editBtn: React.CSSProperties = {
  padding: '9px 20px', background: '#1d2939', color: '#fff', fontSize: 13, fontWeight: 600,
  border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const deleteBtn: React.CSSProperties = {
  padding: '9px 14px', background: '#fff', border: '1px solid #fca5a5', color: '#b91c1c',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
